"use client";

import { ICourse } from "@/api/admin/course";
import { ILeadDropForm } from "@/api/admin/leadDropForm";
import { resendOtp } from "@/api/lead";
import { Input } from "@/components/atoms/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/Select";
// [RUDDERSTACK-OLD] `authSuccess` dropped — its only call (lead submit success) is
// replaced by lead_submit + identifyUser. `ctaClick`/`minimalEdit` still used.
import { ctaClick, minimalEdit } from "@/customAnalytics/trackingEvents";
// ─── RUDDERSTACK (new, sheet-aligned) — remove import + delete rudderStackEvents.ts to revert ───
import {
  trackWidgetImpression,
  trackCtaClick,
  trackLeadformFillstart,
  trackLeadformMobile,
  trackLeadName,
  trackLeadformEmail,
  trackLeadHq,
  trackLeadAoi,
  trackLeadformError,
  trackLeadSubmit,
  trackLeadSubmitFail,
  identifyUser,
} from "@/customAnalytics/rudderStackEvents";
import { WIDGET_NAMES } from "@/customAnalytics/widgetNames";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import {
  LeadEmailValidationError,
  useCreateLead,
} from "@/hooks/useCreateLead";
import { useNonVerifiedCapture } from "@/hooks/useNonVerifiedCapture";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { getLeadEmailFieldError, isLeadEmailFieldValid } from "@/lib/validation";
import { RootState } from "@/store/store";
import { pushToGTMHashed } from "@/utils/utils";
import { ExternalLink } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// [RUDDERSTACK new] form_name for the sheet-aligned lead events fired by lead form 2 (inline).
const FORM_NAME = WIDGET_NAMES.LEAD_FORM_2;

/**
 * Width of the form card, as Tailwind classes.
 *
 * Every consumer except the Banner 2 hero slot renders the card at its original
 * fixed 375px, so this is the default and those call sites are unchanged.
 */
export const LEADDROP_FORM_2_DEFAULT_WIDTH = "w-full max-w-[375px] sm:w-[375px]";

/**
 * Countdown-timer spacing from the Figma banner frames (node 13490:15561):
 * 60x60 boxes on a 325px row, 24px numerals, 9px labels.
 *
 * Opt-in via `timerClassNames`, because those numbers assume the wider embedded
 * slot (440-560px). The form's own default card is 375px, where they would be
 * cramped — so every other consumer keeps the original smaller sizing unless it
 * asks for this.
 */
export const LEADDROP_FORM_2_DESIGN_TIMER = {
  container: "gap-[10px]",
  row: "w-full max-w-[325px]",
  box: "size-[60px] p-[10px]",
  value: "text-[24px] tracking-[0.24px]",
  label: "text-[9px] tracking-[0.09px]",
} as const;

interface LeaddropForm2Props {
  data?: ILeadDropForm | null;
  defaultValues?: any;
  variant?: string;
  /** When true (e.g. blog-with-lead column), hide terms checkbox and do not require acceptance. */
  hideTermsCheckbox?: boolean;
  /**
   * Override the card's width classes. Applied identically to the form, OTP and
   * success screens so the card never resizes as the user moves between them.
   * Defaults to `LEADDROP_FORM_2_DEFAULT_WIDTH` — omit it and nothing changes.
   */
  widthClassName?: string;
  /**
   * Override the countdown timer's spacing. Omit and the timer keeps its
   * original sizing. Pass `LEADDROP_FORM_2_DESIGN_TIMER` from the wide
   * embedded slots, whose numbers assume a 440-560px card.
   */
  timerClassNames?: {
    container?: string;
    row?: string;
    box?: string;
    value?: string;
    label?: string;
  };
}

interface FormErrors {
  mobileNumber?: string;
  name?: string;
  email?: string;
  qualification?: string;
  course?: string;
  terms?: string;
  otp?: string;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getLeaddropForm2Countdown(
  end_date: string | undefined,
  end_time: string | undefined,
): CountdownTime {
  const now = new Date();
  let targetDate: Date;

  if (end_date) {
    const parts = end_date.split("-").map(Number);
    if (parts.length >= 3) {
      targetDate = new Date(parts[0], parts[1] - 1, parts[2]);
    } else {
      targetDate = new Date(end_date);
    }
    if (end_time) {
      const [hours, minutes] = end_time.split(":").map(Number);
      targetDate.setHours(hours ?? 0, minutes ?? 0, 0, 0);
    } else {
      targetDate.setHours(23, 59, 59, 999);
    }
  } else {
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    if (currentMonth < 6) {
      targetDate = new Date(currentYear, 6, 1);
    } else {
      targetDate = new Date(currentYear + 1, 0, 1);
    }
  }

  const diff = targetDate.getTime() - now.getTime();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

const LeaddropForm2 = ({
  defaultValues,
  hideTermsCheckbox = false,
  widthClassName = LEADDROP_FORM_2_DEFAULT_WIDTH,
  timerClassNames,
}: LeaddropForm2Props) => {
  const { lead_drop_form } = useSelector(
    (state: RootState) => state.leaddropform,
  );
  const end_date = (lead_drop_form as any)?.formData?.end_date;
  const end_time = (lead_drop_form as any)?.formData?.end_time;

  const searchParams = useSearchParams() || new URLSearchParams();
  const { toast } = useToast();

  const [step, setStep] = useState<number>(1);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("isUserVerified") === "true"
    ) {
      setStep(2);
    }
  }, []);

  const [formValues, setFormValues] = useState(() => {
    const media =
      (defaultValues || {})?.media || searchParams.get("media") || "Direct";
    const campaign =
      (defaultValues || {})?.campaign ||
      searchParams.get("campaign") ||
      "Direct";
    return {
      phone: "",
      name: "",
      email: "",
      qualification: "",
      course: "",
      interest: "",
      media: media,
      campaign: campaign,
    };
  });

  // Non-verified capture (additive; record-only, does not change this form's flow)
  useNonVerifiedCapture(formValues);

  const [errors, setErrors] = useState<FormErrors>({});
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState<string>("");
  const [otpCountdown, setOtpCountdown] = useState<number>(30);
  const otpInputRef = useRef<HTMLInputElement | null>(null);
  // ─── RUDDERSTACK (new) — widget_impression for the inline (embedded) lead form ───
  const impressionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    return trackWidgetImpression(impressionRef, { widget_name: FORM_NAME });
  }, []);

  const [countdownTime, setCountdownTime] = useState<CountdownTime>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const { handleOtpProcess, verifyOtpAndSubmit, isExistingLeadId, leadsId } =
    useCreateLead();

  useEffect(() => {
    if (defaultValues) {
      const { phone, name, email, qualification, interest, course } =
        defaultValues;

      setFormValues((value: any) => ({
        ...value,
        ...(phone && { phone }),
        ...(name && { name }),
        ...(email && { email }),
        ...(qualification && { qualification }),
        ...(interest && { interest }),
        ...(course && { course }),
      }));
    }
  }, [defaultValues]);

  // OTP resend countdown timer
  useEffect(() => {
    if (showOtp && otpCountdown > 0) {
      const timer = setTimeout(() => {
        setOtpCountdown(otpCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showOtp, otpCountdown]);

  // Focus first OTP input when entering OTP screen
  useEffect(() => {
    if (showOtp && otpSent) {
      const t = setTimeout(() => otpInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [showOtp, otpSent]);

  const computeCountdown = useCallback(
    () => getLeaddropForm2Countdown(end_date, end_time),
    [end_date, end_time],
  );

  useEffect(() => {
    setCountdownTime(computeCountdown());
    const interval = setInterval(() => {
      setCountdownTime(computeCountdown());
    }, 1000);
    return () => clearInterval(interval);
  }, [computeCountdown]);

  const courseErpData = useSelector(
    (state: RootState) => state.globalslice.course,
  );

  const { formData, courses, qualification } = (lead_drop_form as any) || {};

  useEffect(() => {
    const data = courseErpData as ICourse;
    const matchBy = data?.erp_unicode || data?.program_name;
    if (!matchBy || !courses?.length) return;

    const selectedCourse = courses.find(
      (c: ICourse) =>
        c.erp_unicode === data.erp_unicode || c.program_name === data.program_name,
    );

    if (selectedCourse) {
      const courseValue =
        selectedCourse.erp_unicode || selectedCourse.program_name || "";
      setFormValues((prev) => ({ ...prev, course: courseValue }));
      setErrors((prev) => (prev.course ? { ...prev, course: undefined } : prev));
    }
  }, [courseErpData, courses]);

  if (!lead_drop_form) {
    return null;
  }

  if (!formData) {
    return null;
  }

  const {
    title,
    form_title,
    sub_text,
    checkbox_description,
    checkbox_description_color,
    checkbox_color,
    button_text,
    button_color,
    button_text_color,
    button_background_color,
    button_icon,
    button_icon_alt,
    button_step_2_background_color,
    button_step_2_text,
    button_step_2_text_color,
    close_icon,
    close_icon_alt,
    date_background_color,
    date_number_color,
    date_text_color,
    description_color,
    edit_number_text,
    edit_number_text_color,
    form_title_color,
    gradient_end,
    gradient_start,
    new_user_background_image,
    new_user_background_image_alt,
    new_user_subtitle,
    new_user_subtitle_color,
    new_user_title_color,
    otp_description_text,
    otp_description_text_color,
    otp_header_description,
    otp_header_description_color,
    otp_header_text,
    otp_header_text_color,
    otp_title,
    otp_title_color,
    resend_otp_text,
    resend_otp_text_color,
    title_2,
    title_2_color,
    success_icon,
    success_icon_alt,
    success_title,
    success_title_color,
    success_description,
    success_description_text_color,
    success_description_color,
    success_background_image,
    success_background_image_alt,
    success_button_text,
    success_button_icon,
    success_button_icon_alt,
    success_button_text_color,
    success_button_background_color,
    success_button_link,
    success_subtitle,
    success_subtitle_color,
  } = formData;

  // --- Validation ---

  const validateMobileNumber = (value: string): string | undefined => {
    if (!value.trim()) return "Mobile number is required";
    if (!/^\d+$/.test(value)) return "Mobile number must contain only numbers";
    if (value.length !== 10) return "Mobile number must be exactly 10 digits";
    if (!/^[6-9]/.test(value))
      return "Mobile number must start with 6, 7, 8, or 9";
    return undefined;
  };

  const validateName = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length < 2) return "Name must be at least 2 characters";
    if (trimmed.length > 100) return "Name must be at most 100 characters";
    if (!/^[a-zA-Z\s]+$/.test(trimmed))
      return "Name must contain only letters and spaces";
    return undefined;
  };


  const validateQualification = (value: string): string | undefined => {
    if (!value) return "Qualification is required";
    return undefined;
  };

  const validateCourse = (value: string): string | undefined => {
    if (!value) return "Course is required";
    return undefined;
  };

  // --- Handlers ---

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*$/.test(value)) return;
    if (value.length > 10) return;
    if (value.length > 0 && !/^[6-9]/.test(value)) return;

    if (otpSent && value !== formValues.phone) {
      setOtpSent(false);
      setOtpVerified(false);
      setShowOtp(false);
      setOtp("");
      setOtpError("");
    }

    setFormValues({ ...formValues, phone: value });

    if (errors.mobileNumber) {
      const error = validateMobileNumber(value);
      if (!error) setErrors({ ...errors, mobileNumber: undefined });
    }
  };

  const handleMobileBlur = () => {
    const error = validateMobileNumber(formValues.phone);
    setErrors({ ...errors, mobileNumber: error });
    // ─── RUDDERSTACK (new) — completion on valid blur; error on typed-invalid ───
    if (!error) trackLeadformMobile({ form_name: FORM_NAME });
    else if (formValues.phone.length > 0)
      trackLeadformError({ form_name: FORM_NAME, field_name: "mobile", error_message: error });

    if (formValues.phone.length > 0) {
      minimalEdit({
        section: "lead_drop_form",
        component: "mobile_number_input",
        step: "1",
        entity: "mobile_number",
        label: "mobile_number_edit",
      });
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 100) return;
    setFormValues({ ...formValues, name: value });
    const error = validateName(value);
    if (!error && errors.name) setErrors({ ...errors, name: undefined });
  };

  const handleNameBlur = () => {
    const trimmed = formValues.name.trim();
    if (trimmed !== formValues.name) {
      setFormValues((prev) => ({ ...prev, name: trimmed }));
    }
    const error = validateName(trimmed || formValues.name);
    setErrors({ ...errors, name: error });
    // ─── RUDDERSTACK (new) ───
    if (!error) trackLeadName({ form_name: FORM_NAME });
    else if (formValues.name.length > 0)
      trackLeadformError({ form_name: FORM_NAME, field_name: "name", error_message: error });

    if (formValues.name.length > 0) {
      minimalEdit({
        section: "lead_drop_form",
        component: "name_input",
        step: "1",
        entity: "name",
        label: "name_edit",
      });
    }
  };

  const handleEmailChange = (value: string) => {
    if (value.length > 254) return;
    setFormValues({ ...formValues, email: value });
    const error = getLeadEmailFieldError(value);
    if (!error && errors.email) setErrors({ ...errors, email: undefined });

    if (otpSent && value !== formValues.email) {
      setOtpSent(false);
      setOtpVerified(false);
      setShowOtp(false);
      setOtp("");
      setOtpError("");
    }
  };

  const handleEmailBlur = () => {
    const trimmed = formValues.email.trim();
    if (trimmed !== formValues.email) {
      setFormValues((prev) => ({ ...prev, email: trimmed }));
    }
    const error = getLeadEmailFieldError(trimmed || formValues.email);
    setErrors({ ...errors, email: error });
    // ─── RUDDERSTACK (new) ───
    if (!error) trackLeadformEmail({ form_name: FORM_NAME });
    else if (formValues.email.length > 0)
      trackLeadformError({ form_name: FORM_NAME, field_name: "email", error_message: error });

    if (formValues.email.length > 0) {
      minimalEdit({
        section: "lead_drop_form",
        component: "email_input",
        step: "1",
        entity: "email",
        label: "email_edit",
      });
    }
  };

  const handleQualificationChange = (value: string) => {
    setFormValues({ ...formValues, qualification: value });
    const error = validateQualification(value);
    if (error) {
      setErrors({ ...errors, qualification: error });
    } else if (errors.qualification) {
      setErrors({ ...errors, qualification: undefined });
    }
    // ─── RUDDERSTACK (new) ───
    if (value) trackLeadHq({ form_name: FORM_NAME, selected_qualification: value });

    minimalEdit({
      section: "lead_drop_form",
      component: "qualification_select",
      step: "1",
      entity: "qualification",
      label: "qualification_select",
    });
  };

  const handleCourseChange = (value: string) => {
    setFormValues((prev) => {
      if (!value && prev.course) return prev;
      return { ...prev, course: value };
    });
    const error = validateCourse(value);
    if (error && value !== "") setErrors({ ...errors, course: error });
    else if (!error && errors.course) setErrors({ ...errors, course: undefined });
    // ─── RUDDERSTACK (new) ───
    if (value) trackLeadAoi({ form_name: FORM_NAME, selected_program: value });

    if (value) {
      minimalEdit({
        section: "lead_drop_form",
        component: "course_select",
        step: "1",
        entity: "course",
        label: "course_select",
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    const mobileError = validateMobileNumber(formValues.phone);
    if (mobileError) {
      newErrors.mobileNumber = mobileError;
      isValid = false;
    }
    const nameError = validateName(formValues.name);
    if (nameError) {
      newErrors.name = nameError;
      isValid = false;
    }
    const emailError = getLeadEmailFieldError(formValues.email);
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }
    const qualificationError = validateQualification(formValues.qualification);
    if (qualificationError) {
      newErrors.qualification = qualificationError;
      isValid = false;
    }
    const courseError = validateCourse(formValues.course);
    if (courseError) {
      newErrors.course = courseError;
      isValid = false;
    }
    if (!hideTermsCheckbox && !termsAccepted) {
      newErrors.terms = "Please accept the terms and conditions";
      isValid = false;
    }

    setErrors(newErrors);
    // ─── RUDDERSTACK (new) — leadform_error per field shown invalid on submit ───
    const fieldNameMap: Record<string, string> = {
      mobileNumber: "mobile",
      name: "name",
      email: "email",
      qualification: "qualification",
      course: "course",
    };
    Object.entries(newErrors).forEach(([key, msg]) => {
      if (msg && fieldNameMap[key])
        trackLeadformError({
          form_name: FORM_NAME,
          field_name: fieldNameMap[key],
          error_message: String(msg),
        });
    });
    return isValid;
  };

  const isFormValid = (): boolean => {
    const nameTrimmed = formValues.name.trim();
    const termsOk = hideTermsCheckbox || termsAccepted;
    return (
      formValues.phone.trim().length === 10 &&
      /^\d+$/.test(formValues.phone) &&
      /^[6-9]/.test(formValues.phone) &&
      nameTrimmed.length >= 2 &&
      nameTrimmed.length <= 100 &&
      /^[a-zA-Z\s]+$/.test(nameTrimmed) &&
      isLeadEmailFieldValid(formValues.email) &&
      (formValues.qualification?.length ?? 0) > 0 &&
      (formValues.course?.length ?? 0) > 0 &&
      termsOk
    );
  };

  // --- OTP Handlers ---

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*$/.test(value)) return;
    if (value.length > 4) return;
    setOtp(value);
    setOtpError("");

    if (value.length > 0) {
      minimalEdit({
        section: "lead_drop_form",
        component: "otp_input",
        step: "2",
        entity: "otp",
        label: "otp_edit",
      });
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d{0,4}$/.test(pastedData)) {
      setOtp(pastedData);
      setOtpError("");
    }
  };

  const handleResendOtp = async () => {
    if (otpCountdown === 0 && isExistingLeadId) {
      setOtpCountdown(30);
      setOtp("");
      setOtpError("");
      setOtpVerified(false);
      otpInputRef.current?.focus();
      // ─── RUDDERSTACK (new) — cta_click ───
      trackCtaClick({
        widget_name: FORM_NAME,
        cta_name: "resend_otp",
        cta_text: "Resend OTP",
      });

      ctaClick({
        section: "lead_drop_form",
        component: "resend_otp_button",
        label: "resend_otp",
        step: "2",
        entity: "otp_resend",
      });

      try {
        await resendOtp({
          lead_id: isExistingLeadId,
          phoneNumber: formValues.phone,
        });
        toast({
          variant: "default",
          title: "OTP Resent",
          description: "A new OTP has been sent to your mobile number.",
        });
      } catch {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to resend OTP. Please try again.",
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    ctaClick({
      section: "lead_drop_form",
      component: "submit_button",
      label: "submit_form",
      step: showOtp && !otpVerified ? "2" : "1",
      entity: "form_submit",
    });
    // ─── RUDDERSTACK (new) — cta_click (inline: no "continue" step → get_otp / verify_otp) ───
    trackCtaClick({
      widget_name: FORM_NAME,
      cta_name: showOtp && !otpVerified ? "verify_otp" : "get_otp",
      cta_text: showOtp && !otpVerified ? "Verify OTP" : "Request A Call",
    });

    if (!validateForm()) return;

    if (!otpSent && !showOtp) {
      setLoading(true);
      setSendingOtp(true);
      try {
        const response = await handleOtpProcess(formValues);
        if (response) {
          setShowOtp(true);
          setOtpSent(true);
          setOtpCountdown(30);
          toast({
            variant: "default",
            title: "OTP Sent",
            description: "Please enter the OTP sent to your mobile number.",
          });
        }
      } catch (error: unknown) {
        setShowOtp(false);
        if (error instanceof LeadEmailValidationError) {
          const emailMessage = error.message;
          setErrors((prev) => ({ ...prev, email: emailMessage }));
          // ─── RUDDERSTACK (new) — lead_submit_fail (OTP send: email conflict) ───
          trackLeadSubmitFail({ form_name: FORM_NAME, reason: emailMessage });
          return;
        }
        const err = error as { message?: string };
        toast({
          variant: "destructive",
          title: "Error",
          description:
            err?.message || "Failed to send OTP. Please try again.",
        });
        // ─── RUDDERSTACK (new) — lead_submit_fail (OTP send failure) ───
        trackLeadSubmitFail({
          form_name: FORM_NAME,
          reason: err?.message || "Failed to send OTP. Please try again.",
        });
      } finally {
        setLoading(false);
        setSendingOtp(false);
      }
      return;
    }

    if (showOtp && !otpVerified) {
      if (otp.length !== 4) {
        setOtpError("Please enter complete 4 digit OTP");
        // ─── RUDDERSTACK (new) — leadform_error (client-side incomplete OTP) ───
        trackLeadformError({
          form_name: FORM_NAME,
          field_name: "otp",
          error_message: "Please enter complete 4 digit OTP",
        });
        toast({
          variant: "destructive",
          title: "OTP Required",
          description: "Please enter the complete 4 digit OTP.",
        });
        return;
      }

      setLoading(true);
      setOtpError("");
      try {
        const response = await verifyOtpAndSubmit(formValues, otp);

        if (
          response &&
          typeof response === "object" &&
          response?.data?.verified
        ) {
          pushToGTMHashed(formValues.email, formValues.phone, "lead_drop_form");
          // [RUDDERSTACK-OLD] authSuccess (legacy CleverTap `auth_success` + a duplicate
          // identify) — superseded by lead_submit + identifyUser below. To restore: re-add
          // `authSuccess` to the trackingEvents import + uncomment (shape matches Leaddrop9).
          // pushToGTMHashed (Google Ads) is intentionally KEPT.
          // ─── RUDDERSTACK (new, sheet-aligned) — lead_submit + identity for CleverTap ───
          const submittedLeadId = leadsId || isExistingLeadId || "";
          trackLeadSubmit({ form_name: FORM_NAME, lead_id: submittedLeadId });
          identifyUser(formValues.email, {
            name: formValues.name,
            email: formValues.email,
            phone: formValues.phone,
            qualification: formValues.qualification,
            course: formValues.course,
            lead_id: submittedLeadId,
          });
          // ─── /RUDDERSTACK (new) ───
        }

        if (response?.data?.verified) {
          setOtpVerified(true);
          setOtpError("");
          setIsSubmitted(true);
          toast({
            variant: "default",
            title: "Success!",
            description: "Your form has been submitted successfully.",
          });
        } else {
          setOtpVerified(false);
          const reason =
            response?.data?.message ||
            response?.message ||
            "Incorrect code. Please check the OTP and try again.";
          setOtpError(reason);
          // ─── RUDDERSTACK (new) — lead_submit_fail (OTP verify rejected) ───
          trackLeadSubmitFail({ form_name: FORM_NAME, reason });
        }
      } catch (error: unknown) {
        setOtpVerified(false);
        if (error instanceof LeadEmailValidationError) {
          const emailMessage = error.message;
          setErrors((prev) => ({ ...prev, email: emailMessage }));
          setOtpError("");
          return;
        }
        const err = error as { message?: string; response?: { data?: { message?: string } } };
        const reason =
          err?.message ||
          err?.response?.data?.message ||
          "Incorrect code. Please check the OTP and try again.";
        setOtpError(reason);
        // ─── RUDDERSTACK (new) — lead_submit_fail (OTP verify error) ───
        trackLeadSubmitFail({ form_name: FORM_NAME, reason });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (otpVerified) {
      setIsSubmitted(true);
      toast({
        variant: "default",
        title: "Success!",
        description: "Your form has been submitted successfully.",
      });
    }
  };

  // --- Shared Styles ---

  const labelClassName =
    "font-wf-visual-sans text-[12px] font-semibold leading-140 tracking-[0.12px] text-[#222] lg:text-[14px]";

  const inputClassName = cn(
    "w-full rounded-[2px] border-[0.6px] border-[rgba(196,196,196,0.4)] bg-[rgba(196,196,196,0.2)] p-[8px]",
    "font-wf-visual-sans text-[12px] leading-140 tracking-[0.12px] text-[#222]",
    "placeholder:text-[rgba(34,34,34,0.7)]",
    "focus:outline-none focus:ring-1 focus:ring-[#02a7b6]",
    "md:p-[10px] lg:text-[14px]",
  );

  const countdownItems = [
    {
      value: String(countdownTime.days).padStart(2, "0"),
      label: countdownTime.days <= 1 ? "Day" : "Days",
    },
    {
      value: String(countdownTime.hours).padStart(2, "0"),
      label: "Hours",
    },
    {
      value: String(countdownTime.minutes).padStart(2, "0"),
      label: "Minutes",
    },
    {
      value: String(countdownTime.seconds).padStart(2, "0"),
      label: "Seconds",
    },
  ];

  // --- Render: Thank You / Success State (after submit or on refresh when lead was already dropped) ---
  // step === 2 when sessionStorage "isUserVerified" is set (see useEffect), so thank you shows on refresh
  // Layout matches Figma 1080px: title → subtitle → description → Back to Website button → graduate image
  if (isSubmitted || step === 2) {
    const successLine1 = success_description?.includes("\n")
      ? success_description.split("\n")[0]
      : success_description || "We've Received Your Details!";
    const successLine2 = success_description?.includes("\n")
      ? success_description.split("\n").slice(1).join(" ").trim()
      : success_description
        ? null
        : "Our team will reach out soon to help you move forward with your application.";

    const handleBackToWebsite = () => {
      // ─── RUDDERSTACK (new) — cta_click ───
      trackCtaClick({
        widget_name: FORM_NAME,
        cta_name: "back_to_website",
        cta_text: "Back to Website",
      });
      if (typeof window === "undefined") return;
      // An explicit CMS link wins and opens in a new tab.
      const link = String(success_button_link ?? "").trim();
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
        return;
      }
      window.location.href = window.location.origin;
    };

    return (
      <div
        data-track-ignore
        className={cn(
          "flex min-h-[500px] flex-col overflow-hidden rounded-[30px] md:rounded-[28px] lg:rounded-[30px] lg:min-h-[560px] min-[1080px]:min-h-[620px]",
          widthClassName,
        )}
        style={{
          background: `linear-gradient(150deg, ${gradient_start || "#03a4b5"} -2.76%, ${gradient_end || "#342b7c"} 102.4%)`,
        }}
      >
        {/* Content: title, subtitle, description, button */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center md:gap-3 md:px-10 md:py-12 min-[1080px]:gap-3 min-[1080px]:px-12 min-[1080px]:pt-14 min-[1080px]:pb-6">
          <div className="flex flex-col gap-1">
            <h2
              className="font-wf-visual-sans text-[28px] font-bold leading-tight text-white md:text-[32px] lg:text-[36px] min-[1080px]:text-[44px]"
              style={{
                // Was `success_description_color`, which left the CMS's "Success
                // Title Color" field wired to nothing. Both values are #ffffff on
                // every tenant today, so this corrects the wiring without changing
                // any current rendering.
                color: success_title_color || "#ffffff",
              }}
            >
              {success_title || "Thank You"}
            </h2>
            {success_subtitle && (
              <p
                className="font-wf-visual-sans text-[16px] font-semibold text-white md:text-[18px] lg:text-[20px] min-[1080px]:text-[20px]"
                style={{
                  color: success_subtitle_color || "#ffffff",
                }}
              >
                {success_subtitle}
              </p>
            )}
          </div>

          <p
            className="font-wf-visual-sans text-white sm:text-[16px] sm:font-semibold"
            style={{
              color:
                success_description_text_color ||
                success_description_color ||
                "#ffffff",
            }}
          >
            {successLine1}
          </p>
          {successLine2 && (
            <p
              className="font-wf-visual-sans max-w-md text-[12px] leading-relaxed text-white/95 sm:max-w-[480px] sm:text-[16px] sm:font-semibold"
              style={{
                color:
                  success_description_text_color || "rgba(255,255,255,0.95)",
              }}
            >
              {successLine2}
            </p>
          )}
          <button
            type="button"
            onClick={handleBackToWebsite}
            className="font-wf-visual-sans mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-full border px-5 py-[10px] text-[14px] font-semibold text-white shadow-md transition-opacity hover:opacity-90 md:mt-4 min-[1080px]:mt-6 min-[1080px]:px-6 min-[1080px]:py-3"
            style={{
              color: success_button_text_color || "#ffffff",
              borderColor: "rgba(0, 188, 212, 0.9)",
              backgroundColor:
                success_button_background_color || "rgba(33, 58, 83, 0.6)",
            }}
          >
            <span>{success_button_text || "Back to Website"}</span>
            {success_button_icon ? (
              <Image
                src={success_button_icon}
                alt={success_button_icon_alt || "External link"}
                width={20}
                height={20}
                className="shrink-0"
              />
            ) : (
              <ExternalLink className="h-5 w-5 shrink-0" />
            )}
          </button>
        </div>
        {/* success_background_image: used as foreground image right after success messages (name is legacy) */}
        {success_background_image && (
          <div className="relative mt-auto flex shrink-0 justify-center w-full">
            <div className="relative h-48 w-full sm:h-[290px] sm:w-[290px] md:h-[320px] md:w-[320px] lg:h-[320px] lg:w-[320px]">
              <Image
                src={success_background_image}
                alt={success_background_image_alt || "Success"}
                fill
                className="object-cover object-bottom grayscale"
                priority
              />
            </div>
          </div>
        )}
        {/* Graduate / success icon image after success messages */}
        {success_icon && (
          <div className="relative mt-auto flex shrink-0 justify-center w-full">
            <div className="relative h-48 w-full sm:h-[290px] sm:w-[290px] md:h-[338px] md:w-[338px] lg:h-[408px] lg:w-[408px]">
              <Image
                src={success_icon}
                alt={success_icon_alt || "Thank you"}
                fill
                className="object-cover object-bottom grayscale"
                priority
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Render: OTP Screen (Second Screen) ---

  if (showOtp && otpSent) {
    const maskedPhone = `+91 XXXXXXX${formValues.phone.slice(-3)}`;

    const handleChangeNumber = () => {
      // ─── RUDDERSTACK (new) — cta_click ───
      trackCtaClick({
        widget_name: FORM_NAME,
        cta_name: "change_number",
        cta_text: "Change Number",
      });
      setShowOtp(false);
      setOtpSent(false);
      setOtpVerified(false);
      setOtp("");
      setOtpError("");
      setOtpCountdown(30);
    };

    const handleOtpScreenSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (otp.length !== 4) {
        setOtpError("Please enter complete 4 digit OTP");
        return;
      }
      handleSubmit(e);
    };

    return (
      <div
        data-track-ignore
        className={cn(
          "flex min-h-[620px] flex-col gap-[10px] overflow-hidden rounded-[30px] md:min-h-[700px] md:gap-[20px] md:rounded-[28px] lg:min-h-[720px] lg:rounded-[30px]",
          widthClassName,
        )}
        style={{
          background: `linear-gradient(to bottom, ${gradient_start || "#03a4b5"}, ${gradient_end || "#342b7c"} 30%)`,
        }}
      >
        {/* Form title (very first, above timer) + Countdown Timer */}
        <div className="flex shrink-0 flex-col items-center gap-[10px] px-[40px] py-[10px]">
          {(form_title || title) && (
            <h2
              className="font-wf-visual-sans text-center text-[18px] font-bold leading-140 tracking-[0.2px] md:text-[20px] lg:text-[22px] min-[1080px]:text-[12px] min-[1080px]:font-semibold min-[1230px]:text-[12px] min-[1230px]:font-semibold min-[1430px]:text-[14px] min-[1430px]:font-semibold min-[1750px]:text-[16px] min-[1750px]:font-semibold"
              style={{ color: form_title_color || "#fff" }}
            >
              {form_title || title}
            </h2>
          )}
          <div className="flex items-center justify-between w-[256px] md:w-[300px] lg:w-[325px]">
            {countdownItems.map((item, idx) => (
              <div
                key={idx}
                className="flex size-[50px] flex-col items-center justify-center rounded-[10px] p-1 text-center md:size-[60px] md:rounded-[9px] md:p-[9px] lg:rounded-[10px] lg:p-[10px]"
                style={{
                  backgroundColor:
                    date_background_color || "rgba(255,255,255,0.8)",
                }}
              >
                <span
                  className="font-wf-visual-sans text-[20px] font-semibold leading-140 tracking-[0.2px] md:text-[22px] lg:text-[24px]"
                  style={{ color: date_number_color || "#222" }}
                >
                  {item.value}
                </span>
                <span
                  className="font-wf-visual-sans text-[8px] font-normal leading-140 tracking-[0.08px] lg:text-[9px]"
                  style={{ color: date_text_color || "#222" }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* White OTP Card - flex-1 to match first screen height */}
        <div className="flex min-h-0 flex-1 flex-col gap-[10px] rounded-[30px] bg-white p-[20px] md:gap-[20px] md:rounded-[28px] md:p-[24px] lg:rounded-[30px]">
          <div className="flex flex-col gap-2">
            <h3
              className="font-wf-visual-sans text-[20px] font-bold leading-140 tracking-[0.2px] md:text-[22px] lg:text-[24px]"
              style={{ color: otp_header_text_color || "#222" }}
            >
              {otp_header_text || "Enter Verification Code"}
            </h3>
            {otp_header_description && (
              <p
                className="font-wf-visual-sans text-[12px] leading-normal lg:text-[14px]"
                style={{
                  color: otp_header_description_color || "rgba(34,34,34,0.8)",
                }}
              >
                {otp_header_description}
              </p>
            )}
            <p
              className="font-wf-visual-sans text-[12px] leading-normal lg:text-[14px]"
              style={{
                color: otp_description_text_color || "rgba(34,34,34,0.8)",
              }}
            >
              {otp_description_text
                ? `${otp_description_text} ${maskedPhone}`
                : `We've sent a 4-digit OTP to ${maskedPhone}.`}
            </p>
            <button
              type="button"
              onClick={handleChangeNumber}
              className="font-wf-visual-sans w-fit text-[12px] font-semibold leading-140 underline lg:text-[14px]"
              style={{
                color: edit_number_text_color || button_color || "#02a7b6",
              }}
            >
              {edit_number_text || "Change Number"}
            </button>
          </div>

          <form
            onSubmit={handleOtpScreenSubmit}
            className="flex min-h-0 flex-1 flex-col gap-4"
          >
            <div className="flex flex-col gap-1">
              <label
                className={labelClassName}
                style={{ color: otp_title_color || "#222" }}
              >
                {otp_title || "Enter 4-digit OTP Here"}
              </label>
              <div className="flex gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <Input
                    key={index}
                    ref={index === 0 ? otpInputRef : undefined}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[index] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 1);
                      const next = otp.split("");
                      next[index] = v;
                      setOtp(next.join(""));
                      setOtpError("");
                      if (v && index < 3) {
                        const nextEl = e.target
                          .nextElementSibling as HTMLInputElement | null;
                        nextEl?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData
                        .getData("text")
                        .replace(/\D/g, "")
                        .slice(0, 4);
                      setOtp(pasted);
                      setOtpError("");
                      const inputs =
                        e.currentTarget.parentElement?.querySelectorAll(
                          "input",
                        );
                      if (inputs && pasted.length > 0)
                        inputs[Math.min(pasted.length, 3)].focus();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !otp[index] && index > 0) {
                        const prev = (e.target as HTMLInputElement)
                          .previousElementSibling as HTMLInputElement | null;
                        prev?.focus();
                      }
                    }}
                    disabled={otpVerified || sendingOtp}
                    className={cn(
                      "h-[50px] w-[50px] rounded-[2px] border-[0.6px] text-center font-wf-visual-sans text-[18px] font-semibold text-[#222] focus:outline-none focus:ring-1 focus:ring-[#02a7b6]",
                      otpError
                        ? "border-2 border-red-500 bg-red-50/30"
                        : "border-[rgba(196,196,196,0.4)] bg-[rgba(196,196,196,0.2)]",
                    )}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              {otpError && (
                <p
                  className="font-wf-visual-sans text-[12px] font-medium text-red-500"
                  role="alert"
                >
                  {otpError}
                </p>
              )}
            </div>

            <div className="flex items-center gap-1 text-[12px]">
              {otpCountdown > 0 ? (
                <span className="text-[rgba(34,34,34,0.7)]">
                  Didn&apos;t receive the OTP?{" "}
                  <span
                    className="font-semibold"
                    style={{
                      color: resend_otp_text_color || "rgba(34,34,34,0.7)",
                    }}
                  >
                    {resend_otp_text || "Resend OTP"} in {otpCountdown} sec
                  </span>
                </span>
              ) : (
                <span className="text-[rgba(34,34,34,0.7)]">
                  Didn&apos;t receive the OTP?{" "}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="font-semibold underline"
                    style={{
                      color: resend_otp_text_color || button_color || "#02a7b6",
                    }}
                  >
                    {resend_otp_text || "Resend OTP"}
                  </button>
                </span>
              )}
            </div>

            {/* Bottom: full-width Verify button, then consent (per Figma) */}
            <div className="mt-auto flex flex-col gap-3 pt-4">
              <button
                type="submit"
                disabled={loading || otp.length !== 4}
                className={cn(
                  "w-full rounded-[10px] px-[24px] py-[14px] font-wf-visual-sans text-[14px] font-semibold leading-140 -tracking-014 text-white transition-opacity lg:text-[16px] lg:py-[16px]",
                  (loading || otp.length !== 4) &&
                    "cursor-not-allowed opacity-50",
                )}
                style={{
                  backgroundColor:
                    button_step_2_background_color ||
                    button_background_color ||
                    button_color ||
                    "#02a7b6",
                  color:
                    button_step_2_text_color || button_text_color || "#FFFFFF",
                }}
              >
                {loading
                  ? "Verifying..."
                  : button_step_2_text || "Verify & Continue"}
              </button>
              <div className="flex items-start gap-[17px] md:gap-[16px] lg:gap-[17px]">
                <div
                  className={cn(
                    "mt-0.5 flex shrink-0 cursor-pointer items-center justify-center rounded size-6 md:size-[22px] lg:size-6",
                  )}
                  style={{
                    backgroundColor: checkbox_color || "#02a7b6",
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p
                  className="font-wf-visual-sans text-[10px] leading-[1.38] lg:text-[12px]"
                  style={{
                    color: checkbox_description_color || "#272323",
                  }}
                >
                  {checkbox_description ||
                    "By clicking on Request A Call I allow Atlas University to send program communication on email/sms/WhatsApp etc."}
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // --- Render: Form (First Screen) ---

  return (
    <div
      ref={impressionRef}
      data-track-ignore
      className={cn(
        "flex flex-col overflow-hidden rounded-[30px] md:rounded-[28px] lg:rounded-[30px]",
        widthClassName,
      )}
      style={{
        background: `linear-gradient(to bottom, ${gradient_start || "#03a4b5"}, ${gradient_end || "#342b7c"} 30%)`,
      }}
    >
      {/* Form title (very first, above timer) + Countdown Timer */}
      <div
        className={cn(
          "flex shrink-0 flex-col items-center px-[40px] py-[10px]",
          timerClassNames?.container ?? "gap-[6px]",
        )}
      >
        {(form_title || title) && (
          <h2
            className="font-wf-visual-sans text-center text-[18px] font-bold leading-140 tracking-[0.2px] md:text-[20px] lg:text-[22px] min-[1080px]:text-[12px] min-[1080px]:font-semibold min-[1230px]:text-[12px] min-[1230px]:font-semibold min-[1430px]:text-[14px] min-[1430px]:font-semibold min-[1750px]:text-[16px] min-[1750px]:font-semibold"
            style={{ color: form_title_color || "#fff" }}
          >
            {form_title || title}
          </h2>
        )}
        {/* max-w rather than a fixed 325: that is the design width, but the
              card is only 375 wide by default, so a fixed row would overflow the
              narrower consumers. justify-between keeps the design spacing
              wherever it fits. */}
          <div
            className={cn(
              "flex items-center justify-between",
              timerClassNames?.row ?? "w-[240px]",
            )}
          >
          {countdownItems.map((item, idx) => (
            <div
              key={idx}
              className={cn(
                "flex flex-col items-center justify-center rounded-[10px] text-center",
                timerClassNames?.box ?? "h-[50px] w-[54px]",
              )}
              style={{
                backgroundColor:
                  date_background_color || "rgba(255,255,255,0.8)",
              }}
            >
              <span
                className={cn(
                  "font-wf-visual-sans mb-[-3px] font-semibold leading-140",
                  timerClassNames?.value ?? "text-[21px] tracking-[0.21px]",
                )}
                style={{ color: date_number_color || "#222" }}
              >
                {item.value}
              </span>
              <span
                className={cn(
                  "font-wf-visual-sans font-normal leading-140",
                  timerClassNames?.label ?? "text-[8px] tracking-[0.08px]",
                )}
                style={{ color: date_text_color || "#222" }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* White Form Card — title_2 above description */}
      <div className="flex min-h-0 flex-1 flex-col gap-[16px] rounded-[30px] bg-white px-[24px] py-[16px] md:rounded-[28px] lg:rounded-[30px]">
        {(title_2 || sub_text || new_user_subtitle) && (
          <div className="flex flex-col gap-1">
            {title_2 && (
              <h3
                className="font-wf-visual-sans text-[20px] font-bold leading-140 tracking-[0.2px] text-[#222] md:text-[22px] lg:text-[24px]"
                style={{ color: title_2_color || "#222" }}
              >
                {title_2}
              </h3>
            )}
            {(sub_text || new_user_subtitle) && (
              <p
                className="font-wf-visual-sans text-[12px] leading-normal lg:text-[14px]"
                style={{
                  color:
                    description_color ||
                    new_user_subtitle_color ||
                    "rgba(34,34,34,0.8)",
                }}
              >
                {sub_text || new_user_subtitle}
              </p>
            )}
          </div>
        )}

        {/* Form Fields */}
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-[10px]">
            {/* Phone No. */}
            <div className="flex flex-col gap-1">
              <Input
                id="leaddrop-phone"
                type="text"
                inputMode="numeric"
                aria-label="Phone No."
                placeholder="Enter Your Phone No."
                value={formValues.phone}
                onChange={handleMobileChange}
                onBlur={handleMobileBlur}
                onFocus={() =>
                  trackLeadformFillstart({ form_name: FORM_NAME, field_name: "mobile" })
                }
                maxLength={10}
                autoComplete="tel"
                aria-required
                aria-invalid={Boolean(errors.mobileNumber)}
                aria-describedby={
                  errors.mobileNumber ? "leaddrop-phone-error" : undefined
                }
                className={cn(inputClassName, {
                  "border-red-500": errors.mobileNumber,
                })}
              />
              {errors.mobileNumber && (
                <p
                  id="leaddrop-phone-error"
                  className="text-[10px] text-red-500"
                  role="alert"
                >
                  {errors.mobileNumber}
                </p>
              )}
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <Input
                id="leaddrop-name"
                type="text"
                aria-label="Name"
                placeholder="Enter Your Name"
                value={formValues.name}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                onFocus={() =>
                  trackLeadformFillstart({ form_name: FORM_NAME, field_name: "name" })
                }
                maxLength={100}
                autoComplete="name"
                aria-required
                aria-invalid={Boolean(errors.name)}
                aria-describedby={
                  errors.name ? "leaddrop-name-error" : undefined
                }
                className={cn(inputClassName, {
                  "border-red-500": errors.name,
                })}
              />
              {errors.name && (
                <p
                  id="leaddrop-name-error"
                  className="text-[10px] text-red-500"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1">
              <Input
                id="leaddrop-email"
                type="email"
                aria-label="Email"
                placeholder="Enter Your Email"
                value={formValues.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleEmailChange(e.target.value)
                }
                onBlur={handleEmailBlur}
                onFocus={() =>
                  trackLeadformFillstart({ form_name: FORM_NAME, field_name: "email" })
                }
                maxLength={254}
                autoComplete="email"
                aria-required
                aria-invalid={Boolean(errors.email)}
                aria-describedby={
                  errors.email ? "leaddrop-email-error" : undefined
                }
                className={cn(inputClassName, {
                  "border-red-500": errors.email,
                })}
              />
              {errors.email && (
                <p
                  id="leaddrop-email-error"
                  className="text-[10px] text-red-500"
                  role="alert"
                >
                  {errors.email}
                </p>
              )}
            </div>

            {/* Highest Qualifications */}
            <div className="flex flex-col gap-1">
              <Select
                value={formValues.qualification}
                onValueChange={handleQualificationChange}
                onOpenChange={(open) => {
                  if (open)
                    trackLeadformFillstart({ form_name: FORM_NAME, field_name: "qualification" });
                }}
                disabled={!qualification || qualification.length === 0}
              >
                <SelectTrigger
                  aria-required
                  aria-invalid={Boolean(errors.qualification)}
                  aria-describedby={
                    errors.qualification
                      ? "leaddrop-qualification-error"
                      : undefined
                  }
                  aria-label="Highest Qualifications"
                  className={cn(
                    "w-full rounded-[2px] border-[0.6px] border-[rgba(196,196,196,0.4)] bg-[rgba(196,196,196,0.2)] px-[8px] py-[4px]",
                    "font-wf-visual-sans text-[12px] leading-140 -tracking-012 text-[#222] data-placeholder:text-[rgba(34,34,34,0.7)]",
                    "flex items-center justify-between",
                    "focus:outline-none focus:ring-1 focus:ring-[#02a7b6]",
                    "md:p-[10px] lg:text-[14px]",
                    {
                      "border-red-500": errors.qualification,
                      "cursor-not-allowed opacity-50":
                        !qualification || qualification.length === 0,
                    },
                  )}
                >
                  <SelectValue placeholder="Enter Highest Qualifications" />
                </SelectTrigger>
                {qualification && qualification.length > 0 && (
                  <SelectContent className="w-(--radix-select-trigger-width) rounded-lg bg-white shadow-lg">
                    {qualification.map((option: any) => (
                      <SelectItem
                        key={option.id || option.name}
                        value={option.value || option.id || String(option.id)}
                        className="cursor-pointer px-3 py-2 text-[12px] lg:text-[14px]"
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                )}
              </Select>
              {errors.qualification && (
                <p
                  id="leaddrop-qualification-error"
                  className="text-[10px] text-red-500"
                  role="alert"
                >
                  {errors.qualification}
                </p>
              )}
            </div>

            {/* Area of Interest (Course) */}
            <div className="flex flex-col gap-1">
              <Select
                value={formValues.course}
                onValueChange={handleCourseChange}
                onOpenChange={(open) => {
                  if (open)
                    trackLeadformFillstart({ form_name: FORM_NAME, field_name: "course" });
                }}
                disabled={!courses || courses.length === 0}
              >
                <SelectTrigger
                  aria-required
                  aria-invalid={Boolean(errors.course)}
                  aria-describedby={
                    errors.course ? "leaddrop-course-error" : undefined
                  }
                  aria-label="Area of Interest"
                  className={cn(
                    "w-full rounded-[2px] border-[0.6px] border-[rgba(196,196,196,0.4)] bg-[rgba(196,196,196,0.2)] p-[8px]",
                    "font-wf-visual-sans text-[12px] leading-140 -tracking-012 text-[#222] data-placeholder:text-[rgba(34,34,34,0.7)]",
                    "flex items-center justify-between",
                    "focus:outline-none focus:ring-1 focus:ring-[#02a7b6]",
                    "md:p-[10px] lg:text-[14px]",
                    {
                      "border-red-500": errors.course,
                      "cursor-not-allowed opacity-50":
                        !courses || courses.length === 0,
                    },
                  )}
                >
                  <SelectValue placeholder="Enter Area of Interest" />
                </SelectTrigger>
                {courses && courses.length > 0 && (
                  <SelectContent className="w-(--radix-select-trigger-width) rounded-lg bg-white shadow-lg">
                    {courses.map((option: any) => (
                      <SelectItem
                        key={option.erp_unicode || option.program_name}
                        value={option.erp_unicode || option.program_name}
                        className="cursor-pointer px-3 py-2 text-[12px] lg:text-[14px]"
                      >
                        {option.program_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                )}
              </Select>
              {errors.course && (
                <p
                  id="leaddrop-course-error"
                  className="text-[10px] text-red-500"
                  role="alert"
                >
                  {errors.course}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || sendingOtp || !isFormValid()}
            className={cn(
              "mt-[16px] w-full rounded-[10px] px-[24px] py-[12px]",
              "font-wf-visual-sans text-[14px] font-semibold leading-140 -tracking-014 text-white",
              "transition-opacity lg:text-[16px]",
              {
                "cursor-not-allowed opacity-50":
                  loading || sendingOtp || !isFormValid(),
              },
            )}
            style={{
              backgroundColor:
                button_background_color || button_color || "#02a7b6",
              color: button_text_color || "#FFFFFF",
            }}
          >
            {sendingOtp
              ? "Sending OTP..."
              : loading && showOtp && !otpVerified
                ? "Verifying..."
                : button_text || "Request A Call"}
            {button_icon && (
              <Image
                src={button_icon}
                alt={button_icon_alt || "Submit"}
                width={20}
                height={20}
                className="ml-2 inline-block"
              />
            )}
          </button>

          {/* Disclaimer Checkbox */}
          {!hideTermsCheckbox && (
            <>
              <div className="mt-[8px] flex items-start gap-[8px]">
                <div
                  onClick={() => {
                    setTermsAccepted(!termsAccepted);

                    minimalEdit({
                      section: "lead_drop_form",
                      component: "terms_checkbox",
                      step: "1",
                      entity: "terms",
                      label: !termsAccepted ? "terms_accepted" : "terms_unchecked",
                    });

                    if (errors.terms) {
                      setErrors({ ...errors, terms: undefined });
                    }
                  }}
                  className={cn(
                    "mt-0.5 flex shrink-0 cursor-pointer items-center justify-center rounded",
                    "size-6 md:size-[22px] lg:size-6",
                    !termsAccepted &&
                      "border-2 border-[rgba(196,196,196,0.6)] bg-white",
                  )}
                  style={
                    termsAccepted
                      ? { backgroundColor: checkbox_color || "#02a7b6" }
                      : undefined
                  }
                >
                  {termsAccepted && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </div>
                <p
                  className="font-wf-visual-sans text-[10px] leading-[1.38] lg:text-[12px]"
                  style={{ color: checkbox_description_color || "#272323" }}
                >
                  {checkbox_description ||
                    "By clicking on Request A Call I allow Atlas University to send program communication on email/sms/WhatsApp etc."}
                </p>
              </div>
              {errors.terms && (
                <p className="ml-10 mt-1 text-[10px] text-red-500">
                  {errors.terms}
                </p>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export function LeaddropForm2MobileAboutCta() {
  const dispatch = useDispatch();
  const { lead_drop_form } = useSelector(
    (state: RootState) => state.leaddropform,
  );
  const [countdownTime, setCountdownTime] = useState<CountdownTime>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const end_date = (lead_drop_form as any)?.formData?.end_date;
  const end_time = (lead_drop_form as any)?.formData?.end_time;
  const { formData } = (lead_drop_form as any) || {};

  const updateCountdown = useCallback(
    () => setCountdownTime(getLeaddropForm2Countdown(end_date, end_time)),
    [end_date, end_time],
  );

  useEffect(() => {
    updateCountdown();
    const interval = setInterval(() => {
      updateCountdown();
    }, 1000);
    return () => clearInterval(interval);
  }, [updateCountdown]);

  if (!lead_drop_form || !formData) {
    return null;
  }

  const {
    title,
    form_title,
    form_title_color,
    gradient_end,
    gradient_start,
    button_text,
    button_text_color,
    button_background_color,
    button_icon,
    button_icon_alt,
    date_background_color,
    date_number_color,
    date_text_color,
  } = formData;

  const heading = form_title || title;
  const countdownItems = [
    {
      value: String(countdownTime.days).padStart(2, "0"),
      label: countdownTime.days <= 1 ? "Day" : "Days",
    },
    {
      value: String(countdownTime.hours).padStart(2, "0"),
      label: "Hours",
    },
    {
      value: String(countdownTime.minutes).padStart(2, "0"),
      label: "Minutes",
    },
    {
      value: String(countdownTime.seconds).padStart(2, "0"),
      label: "Seconds",
    },
  ];

  const ctaBackground =
    button_background_color ?? "#FFFFFF";
  const ctaTextColor = button_text_color ?? "#222222";

  return (
    <>
      <div
        className="font-wf-visual-sans flex w-full max-w-[400px] flex-col gap-[10px] rounded-t-[20px] rounded-b-[10px] px-0.5 pb-0.5 pt-[10px] sm:max-w-[440px]"
        style={{
          background: `linear-gradient(to bottom, ${gradient_start || "#02a7b6"}, ${gradient_end || "#342b7c"} 100%)`,
        }}
      >
        {heading && (
          <p
            className="w-full min-w-0 text-center text-[12px] font-semibold leading-[1.4] tracking-[0.12px] text-white"
            style={{ color: form_title_color || "#ffffff" }}
          >
            {heading}
          </p>
        )}
        <div className="mx-auto flex w-[256px] max-w-full items-center justify-between">
          {countdownItems.map((item, idx) => (
            <div
              key={idx}
              className="flex size-[50px] shrink-0 flex-col items-center justify-center rounded-[10px] p-1 text-center"
              style={{
                backgroundColor:
                  date_background_color || "rgba(255, 255, 255, 0.8)",
              }}
            >
              <span
                className="w-full text-[20px] font-semibold leading-[1.4] tracking-[0.2px]"
                style={{ color: date_number_color || "#222" }}
              >
                {item.value}
              </span>
              <span
                className="w-full text-[8px] font-normal leading-[1.4] tracking-[0.08px]"
                style={{ color: date_text_color || "#222" }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-track-ignore
          onClick={() => {
            // ─── RUDDERSTACK (new) — cta_click ───
            trackCtaClick({
              widget_name: FORM_NAME,
              cta_name: "apply_now",
              cta_text: String(button_text || "Apply Now"),
            });
            ctaClick({
              section: "about_section",
              component: "mobile_lead_form2_cta",
              label: button_text || "Apply Now",
              step: "1",
              entity: "about_section",
            });
            dispatch(
              handleOpenLeaddrop({
                variant: LEAD_DROP_VARIANTS.REGISTER,
              }),
            );
          }}
          className="font-wf-visual-sans flex w-full shrink-0 items-center justify-center gap-2 rounded-[10px] px-6 py-4 text-[16px] font-semibold leading-[1.4] tracking-[0.16px] transition-opacity hover:opacity-95"
          style={{
            backgroundColor: ctaBackground,
            color: ctaTextColor,
          }}
        >
          {button_text || "Apply Now"}
          {button_icon && (
            <Image
              src={button_icon}
              alt={button_icon_alt || ""}
              width={20}
              height={20}
              className="shrink-0"
            />
          )}
        </button>
      </div>
    </>
  );
}

export default LeaddropForm2;
